import {
    Page,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Collapsible,
    Link,
    Divider,
    List,
} from '@shopify/polaris';
import React, { useState, useCallback } from 'react';

const FaqPage = () => {
    const [openSections, setOpenSections] = useState({});

    const toggleSection = useCallback((sectionId) => {
        setOpenSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    }, []);

    const faqSections = [
        {
            id: 'general',
            title: 'General Questions',
            anchor: 'general-questions',
            items: [
                {
                    id: 'what-is',
                    question: 'What is LM Store Protector?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                LM Store Protector is a comprehensive security extension for your Shopify store that helps protect your content from unauthorized copying, printing, and access. It provides multiple layers of protection including:
                            </Text>
                            <List type="bullet">
                                <List.Item>
                                    <Text fontWeight="semibold">Content Protection</Text>: Prevents copying, printing, and right-clicking
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Developer Tools Blocking</Text>: Stops users from accessing browser developer tools
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Geo-Blocking</Text>: Restricts access based on geographic location (country, state, city)
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Redirect Prevention</Text>: Controls external navigation from your store
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Screenshot Protection</Text>: Blurs content when users switch tabs or minimize windows
                                </List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'why-need',
                    question: 'Why do I need Store Protector?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Store Protector helps protect your:
                            </Text>
                            <List type="bullet">
                                <List.Item><Text fontWeight="semibold">Product images</Text> from being downloaded or copied</List.Item>
                                <List.Item><Text fontWeight="semibold">Product descriptions</Text> and unique content from plagiarism</List.Item>
                                <List.Item><Text fontWeight="semibold">Pricing information</Text> from competitors</List.Item>
                                <List.Item><Text fontWeight="semibold">Store design</Text> from being replicated</List.Item>
                                <List.Item><Text fontWeight="semibold">Intellectual property</Text> from unauthorized use</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'performance',
                    question: 'Will this affect my store\'s performance?',
                    answer: (
                        <Text>
                            No. Store Protector is optimized for performance and uses modern web technologies. It runs efficiently without slowing down your store's loading speed or user experience.
                        </Text>
                    ),
                },
                {
                    id: 'mobile-compatibility',
                    question: 'Does it work on mobile devices?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! Store Protector is fully compatible with mobile devices including:
                            </Text>
                            <List type="bullet">
                                <List.Item>iOS Safari</List.Item>
                                <List.Item>Chrome Mobile</List.Item>
                                <List.Item>Samsung Internet</List.Item>
                                <List.Item>Firefox Mobile</List.Item>
                                <List.Item>All major mobile browsers</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
        {
            id: 'copy-print',
            title: 'Copy & Print Protection',
            anchor: 'copy--print-protection',
            items: [
                {
                    id: 'copy-protection-does',
                    question: 'What does copy protection do?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Copy protection prevents users from:
                            </Text>
                            <List type="bullet">
                                <List.Item>Copying text from your store (Ctrl+C / Cmd+C)</List.Item>
                                <List.Item>Right-clicking to access context menus</List.Item>
                                <List.Item>Dragging and dropping images</List.Item>
                                <List.Item>Using keyboard shortcuts like Ctrl+A (Select All), Ctrl+X (Cut)</List.Item>
                                <List.Item>Printing pages (Ctrl+P / Cmd+P)</List.Item>
                                <List.Item>Viewing page source code (Ctrl+U / Cmd+U)</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'forms-work',
                    question: 'Will customers still be able to use forms and input fields?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! Store Protector is smart enough to allow normal functionality on:
                            </Text>
                            <List type="bullet">
                                <List.Item>Contact forms</List.Item>
                                <List.Item>Search boxes</List.Item>
                                <List.Item>Checkout forms</List.Item>
                                <List.Item>Any input fields (INPUT, TEXTAREA, SELECT)</List.Item>
                            </List>
                            <Text>
                                Users can still paste, edit, and interact normally with form fields.
                            </Text>
                        </BlockStack>
                    ),
                },
                {
                    id: 'what-happens-copy',
                    question: 'What happens when someone tries to copy?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                When someone attempts to copy content:
                            </Text>
                            <List type="number">
                                <List.Item>The copy action is blocked</List.Item>
                                <List.Item>A warning message is displayed</List.Item>
                                <List.Item>The clipboard is overwritten with a protection message instead of your content</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'customize-messages',
                    question: 'Can I customize the warning messages?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! You can customize all warning messages through the app configuration:
                            </Text>
                            <List type="bullet">
                                <List.Item>Copy attempt messages</List.Item>
                                <List.Item>Right-click messages</List.Item>
                                <List.Item>Keyboard shortcut messages</List.Item>
                                <List.Item>Developer tools messages</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
        {
            id: 'devtools',
            title: 'Developer Tools Protection',
            anchor: 'developer-tools-protection',
            items: [
                {
                    id: 'what-devtools',
                    question: 'What are Developer Tools?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Developer Tools (DevTools) are browser features that allow users to:
                            </Text>
                            <List type="bullet">
                                <List.Item>Inspect HTML code</List.Item>
                                <List.Item>View CSS styles</List.Item>
                                <List.Item>Access JavaScript</List.Item>
                                <List.Item>See network requests</List.Item>
                                <List.Item>Debug websites</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'why-block-devtools',
                    question: 'Why should I block Developer Tools?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Blocking DevTools helps prevent:
                            </Text>
                            <List type="bullet">
                                <List.Item>Competitors from copying your code</List.Item>
                                <List.Item>Users from bypassing your protection</List.Item>
                                <List.Item>Unauthorized access to your store's technical details</List.Item>
                                <List.Item>Reverse engineering of your store's functionality</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'how-devtools-work',
                    question: 'How does DevTools protection work?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Store Protector uses multiple detection methods:
                            </Text>
                            <List type="bullet">
                                <List.Item>
                                    <Text fontWeight="semibold">Debugger traps</Text>: Detects when DevTools are opened
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Window size detection</Text>: Monitors for DevTools panels
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Console detection</Text>: Identifies console usage
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Object pattern detection</Text>: Finds DevTools-related objects
                                </List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'block-legitimate',
                    question: 'Will this block legitimate users?',
                    answer: (
                        <Text>
                            No. Regular customers don't need Developer Tools to browse and shop. The protection only activates when someone tries to open DevTools, not during normal browsing.
                        </Text>
                    ),
                },
                {
                    id: 'what-happens-devtools',
                    question: 'What happens when someone tries to open DevTools?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                When DevTools are detected:
                            </Text>
                            <List type="number">
                                <List.Item>A warning alert is displayed</List.Item>
                                <List.Item>The page content is replaced with a protection message</List.Item>
                                <List.Item>The console is cleared</List.Item>
                                <List.Item>The user is prevented from accessing DevTools</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
        {
            id: 'geoblocking',
            title: 'Geo-Blocking',
            anchor: 'geo-blocking',
            items: [
                {
                    id: 'what-geoblocking',
                    question: 'What is Geo-Blocking?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Geo-Blocking allows you to restrict access to your store based on the visitor's geographic location. You can block:
                            </Text>
                            <List type="bullet">
                                <List.Item>Entire countries</List.Item>
                                <List.Item>Specific states/provinces</List.Item>
                                <List.Item>Individual cities</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'why-geoblocking',
                    question: 'Why would I use Geo-Blocking?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Common use cases:
                            </Text>
                            <List type="bullet">
                                <List.Item>
                                    <Text fontWeight="semibold">Legal compliance</Text>: Restrict access in regions where you can't legally sell
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Licensing restrictions</Text>: Block countries where you don't have distribution rights
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Competitor protection</Text>: Prevent access from specific competitor locations
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Market control</Text>: Limit access to your target markets only
                                </List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'what-happens-blocked',
                    question: 'What happens when someone from a blocked location visits?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                When a blocked visitor tries to access your store:
                            </Text>
                            <List type="number">
                                <List.Item>A warning message is displayed</List.Item>
                                <List.Item>They cannot browse your store</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'multiple-countries',
                    question: 'Can I block multiple countries?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! You can block:
                            </Text>
                            <List type="bullet">
                                <List.Item>A single country</List.Item>
                                <List.Item>Multiple countries (array)</List.Item>
                                <List.Item>Specific states within countries</List.Item>
                                <List.Item>Specific cities</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
        {
            id: 'redirect',
            title: 'Redirect Prevention',
            anchor: 'redirect-prevention',
            items: [
                {
                    id: 'what-redirect',
                    question: 'What is Redirect Prevention?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Redirect Prevention controls which external websites users can navigate to from your store. You can:
                            </Text>
                            <List type="bullet">
                                <List.Item>Block all external links</List.Item>
                                <List.Item>Allow specific trusted domains</List.Item>
                                <List.Item>Control form submissions to external URLs</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'why-redirect',
                    question: 'Why would I use Redirect Prevention?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Use cases include:
                            </Text>
                            <List type="bullet">
                                <List.Item>
                                    <Text fontWeight="semibold">Preventing phishing</Text>: Stop malicious links from redirecting customers
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Controlling navigation</Text>: Keep users on your store
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Security</Text>: Prevent unauthorized external redirects
                                </List.Item>
                                <List.Item>
                                    <Text fontWeight="semibold">Brand protection</Text>: Control where customers go from your site
                                </List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'social-media',
                    question: 'Does it block social media links?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                By default, yes. If you want to allow social media links, add them to your allowed domains list:
                            </Text>
                            <List type="bullet">
                                <List.Item>Facebook: <Text fontWeight="semibold">facebook.com</Text></List.Item>
                                <List.Item>Instagram: <Text fontWeight="semibold">instagram.com</Text></List.Item>
                                <List.Item>Twitter: <Text fontWeight="semibold">twitter.com</Text></List.Item>
                                <List.Item>etc.</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'email-phone',
                    question: 'What about email and phone links?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Special links are automatically allowed:
                            </Text>
                            <List type="bullet">
                                <List.Item><Text fontWeight="semibold">mailto:</Text> links (email)</List.Item>
                                <List.Item><Text fontWeight="semibold">tel:</Text> links (phone)</List.Item>
                                <List.Item><Text fontWeight="semibold">#</Text> links (anchor links)</List.Item>
                                <List.Item><Text fontWeight="semibold">javascript:</Text> links (if needed)</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
        {
            id: 'mobile',
            title: 'Mobile Devices',
            anchor: 'mobile-devices',
            items: [
                {
                    id: 'mobile-work',
                    question: 'Does Store Protector work on mobile?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! Store Protector is fully optimized for mobile devices with special handling for:
                            </Text>
                            <List type="bullet">
                                <List.Item>Touch events</List.Item>
                                <List.Item>Long-press gestures (mobile equivalent of right-click)</List.Item>
                                <List.Item>Mobile keyboard shortcuts</List.Item>
                                <List.Item>Mobile browser compatibility</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'mobile-rightclick',
                    question: 'How does right-click protection work on mobile?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                On mobile devices:
                            </Text>
                            <List type="bullet">
                                <List.Item>
                                    <Text fontWeight="semibold">Long-press</Text> (holding finger on screen) is blocked
                                </List.Item>
                                <List.Item>Users see the same warning as desktop right-click</List.Item>
                                <List.Item>Form fields remain functional (users can still paste/edit)</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
                {
                    id: 'mobile-normal-use',
                    question: 'Can mobile users still use my store normally?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes! All normal shopping functions work:
                            </Text>
                            <List type="bullet">
                                <List.Item>Browsing products</List.Item>
                                <List.Item>Adding to cart</List.Item>
                                <List.Item>Checkout process</List.Item>
                                <List.Item>Form submissions</List.Item>
                                <List.Item>Search functionality</List.Item>
                            </List>
                            <Text>
                                Only protection-related actions are blocked.
                            </Text>
                        </BlockStack>
                    ),
                },
                {
                    id: 'tablets',
                    question: 'Does it work on tablets?',
                    answer: (
                        <BlockStack gap="300">
                            <Text>
                                Yes, Store Protector works on:
                            </Text>
                            <List type="bullet">
                                <List.Item>iPads</List.Item>
                                <List.Item>Android tablets</List.Item>
                                <List.Item>All tablet devices</List.Item>
                            </List>
                        </BlockStack>
                    ),
                },
            ],
        },
    ];

    const renderFaqItem = (item) => {
        const isOpen = openSections[item.id] || false;
        return (
            <Card key={item.id}>
                <BlockStack gap="200">
                    <button
                        onClick={() => toggleSection(item.id)}
                        style={{
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            padding: 0,
                            width: '100%',
                        }}
                    >
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" fontWeight="semibold" as="h3">
                                {item.question}
                            </Text>
                            <Text variant="headingMd" tone="subdued">
                                {isOpen ? '−' : '+'}
                            </Text>
                        </InlineStack>
                    </button>
                    <Collapsible
                        open={isOpen}
                        id={`faq-${item.id}`}
                        transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
                    >
                        <BlockStack gap="300" style={{ paddingTop: '16px' }}>
                            {item.answer}
                        </BlockStack>
                    </Collapsible>
                </BlockStack>
            </Card>
        );
    };

    return (
        <Page title="Frequently Asked Questions (FAQ)">
            <BlockStack gap="500">
                {/* FAQ Sections */}
                {faqSections.map((section) => (
                    <BlockStack key={section.id} gap="400" id={section.anchor}>
                        <Card>
                            <Text variant="headingLg" fontWeight="bold" as="h2">
                                {section.title}
                            </Text>
                            <br />
                            <BlockStack gap="300">
                                {section.items.map((item) => renderFaqItem(item))}
                            </BlockStack>
                            <Divider />
                        </Card>
                    </BlockStack>
                ))}
            </BlockStack>
            <br />
        </Page>
    );
};

export default FaqPage;
